import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { NavigationButton } from '@/components/NavigationButton';
import {
  ECommunityAccessConfig,
  EEventsFeedAccessConfig,
} from '@/graphql-types/globalTypes';
import { useElementHeight } from '@/hooks/useElementHeight';
import { useIntutoLearningNavVisible } from '@/hooks/useIntutoLearningNavVisible';
import { useMemberLearningContextOnSimpleEventRoute } from '@/hooks/useMemberLearningContextOnSimpleEventRoute';
import { usePermissions } from '@/hooks/usePermissions';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import {
  BookIcon,
  CalendarIcon,
  CornerUpLeftIcon,
  FolderIcon,
  XIcon,
  ZapIcon,
} from '@/imports/ui/chakra/feather';
import {
  Blueprint,
  BlueprintPortal,
  useBlueprint,
} from '@/layouts/components/Blueprint';

import { CoffeeNav_Tenant } from './graphql-types/CoffeeNav_Tenant';

import { MyEvents } from './components/MyEvents';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const CoffeeContext = React.createContext<{ footerHeight: number }>({
  footerHeight: 0,
});

const TENANT = gql`
  query CoffeeNav_Tenant {
    tenant {
      result {
        _id
        eventsFeedConfig
      }
    }
  }
`;

const buildPath = ({
  filter,
  includeMine,
}: {
  filter: 'upcoming' | 'past';
  includeMine?: boolean;
}) =>
  includeMine
    ? `/events?filter=${filter}&includeCurrentUserInvolvedEventsOnly=true`
    : `/events?filter=${filter}`;

const buildXproPath = ({ filter }: { filter: 'upcoming' | 'past' }) =>
  `/events/xpro-conferences?filter=${filter}`;

type MobileNavButtonProps = {
  label: string;
  icon: React.ReactElement;
  isActive: boolean;
  options: Array<{
    label: string;
    isActive: boolean;
    onClick: () => void;
  }>;
};

const MobileNavButton: React.FC<MobileNavButtonProps> = ({
  label,
  icon,
  isActive,
  options,
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onToggle}
      onClose={onClose}
      placement="right-start"
      closeOnBlur
      isLazy
    >
      <Tooltip label={label} placement="right" hasArrow openDelay={150}>
        <PopoverTrigger>
          <IconButton
            aria-label={label}
            icon={icon}
            borderRadius="full"
            colorScheme={isActive ? 'primary' : 'gray'}
            variant={isActive ? 'solid' : 'outline'}
            size="lg"
          />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent w="60" zIndex="popover">
        <PopoverArrow />
        <PopoverBody>
          <Stack spacing="3">
            <Text fontWeight="semibold" color="gray.700">
              {label}
            </Text>
            <Stack spacing="2">
              {options.map((option) => (
                <Button
                  key={option.label}
                  onClick={() => {
                    option.onClick();
                    onClose();
                  }}
                  justifyContent="flex-start"
                  variant={option.isActive ? 'solid' : 'ghost'}
                  colorScheme={option.isActive ? 'primary' : 'gray'}
                >
                  {option.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const MobileNav: React.FC<{
  canViewEvents: boolean;
  showEventsSidebarNav: boolean;
  showLearningSidebarNav: boolean;
  isAllEvents: boolean;
  isMyEvents: boolean;
  isXproConferences: boolean;
  isUpcoming: boolean;
  isPast: boolean;
  isLearning: boolean;
  isLearningCoursesActive: boolean;
  learningPath: string;
  onNavigate: (path: string) => void;
  headerHeight: number;
}> = ({
  canViewEvents,
  showEventsSidebarNav,
  showLearningSidebarNav,
  isAllEvents,
  isMyEvents,
  isXproConferences,
  isUpcoming,
  isPast,
  isLearning,
  isLearningCoursesActive,
  learningPath,
  onNavigate,
  headerHeight,
}) => {
  if (!canViewEvents) return null;
  if (!showEventsSidebarNav && !showLearningSidebarNav) return null;

  return (
    <Box
      position="fixed"
      top={`${headerHeight}px`}
      left="0"
      bottom="0"
      // Keep above page content so Learning/Events popovers aren't covered.
      // Webflow hamburger still wins via Blueprint header z-index 10000.
      zIndex="docked"
      display={{ base: 'flex', lg: 'none' }}
      flexDirection="column"
      gap="3"
      alignItems="center"
      width="60px"
      bg="white"
      pt="3"
      pb="3"
      boxShadow="base"
      // _before={{
      //   content: "''",
      //   position: 'absolute',
      //   top: '8px',
      //   bottom: '8px',
      //   width: '2px',
      //   backgroundColor: 'gray.100',
      //   left: '50%',
      //   transform: 'translateX(-50%)',
      // }}
    >
      {showEventsSidebarNav ? (
        <>
          <MobileNavButton
            label="All Events"
            icon={<CalendarIcon />}
            isActive={isAllEvents}
            options={[
              {
                label: 'Upcoming Events',
                isActive: isAllEvents && isUpcoming,
                onClick: () => onNavigate(buildPath({ filter: 'upcoming' })),
              },
              {
                label: 'Past Events',
                isActive: isAllEvents && isPast,
                onClick: () => onNavigate(buildPath({ filter: 'past' })),
              },
            ]}
          />
          <MobileNavButton
            label="My Events"
            icon={<CornerUpLeftIcon />}
            isActive={isMyEvents}
            options={[
              {
                label: 'Upcoming Events',
                isActive: isMyEvents && isUpcoming,
                onClick: () =>
                  onNavigate(
                    buildPath({ filter: 'upcoming', includeMine: true })
                  ),
              },
              {
                label: 'Past Events',
                isActive: isMyEvents && isPast,
                onClick: () =>
                  onNavigate(buildPath({ filter: 'past', includeMine: true })),
              },
            ]}
          />
          <MobileNavButton
            label="XPro Conference Events"
            icon={<XIcon />}
            isActive={isXproConferences}
            options={[
              {
                label: 'Upcoming Events',
                isActive: isXproConferences && isUpcoming,
                onClick: () =>
                  onNavigate(buildXproPath({ filter: 'upcoming' })),
              },
              {
                label: 'Past Events',
                isActive: isXproConferences && isPast,
                onClick: () => onNavigate(buildXproPath({ filter: 'past' })),
              },
            ]}
          />
        </>
      ) : null}
      {showLearningSidebarNav ? (
        <MobileNavButton
          label="Learning"
          icon={<BookIcon />}
          isActive={isLearning}
          options={[
            {
              label: 'Courses',
              isActive: isLearningCoursesActive,
              onClick: () => onNavigate('/learning'),
            },
            {
              label: 'Collections',
              isActive: learningPath.startsWith('/learning/collections'),
              onClick: () => onNavigate('/learning/collections'),
            },
            {
              label: 'My courses',
              isActive: learningPath === '/learning/my-courses',
              onClick: () => onNavigate('/learning/my-courses'),
            },
            {
              label: 'My collections',
              isActive: learningPath === '/learning/my-collections',
              onClick: () => onNavigate('/learning/my-collections'),
            },
          ]}
        />
      ) : null}
    </Box>
  );
};

export const CoffeeNav: React.FC = () => {
  const router = useRouter();
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();
  const permissions = usePermissions();
  const query = useQuery<CoffeeNav_Tenant>(TENANT);
  const intutoLearningNav = useIntutoLearningNavVisible();

  const isMyEvents = router.asPath.includes(
    'includeCurrentUserInvolvedEventsOnly=true'
  );
  const isXproConferences = router.asPath.includes('/events/xpro-conferences');
  const isAllEvents =
    !isMyEvents &&
    !isXproConferences &&
    !router.asPath.includes('includeCurrentUserInvolvedEventsOnly=true');
  const isUpcoming = router.asPath.includes('filter=upcoming');
  const isPast = router.asPath.includes('filter=past');

  const learningPath = router.asPath.split('?')[0] ?? '';
  const learningContextOnEventRoute =
    useMemberLearningContextOnSimpleEventRoute();
  const isOnLearningRoute =
    learningPath === '/learning' || learningPath.startsWith('/learning/');
  const isLearningCourses =
    learningPath === '/learning' || learningContextOnEventRoute;
  const isLearningCollections = learningPath.startsWith(
    '/learning/collections'
  );

  const canViewEvents =
    permissions.isAuthenticated ||
    query.data?.tenant.result?.eventsFeedConfig ===
      EEventsFeedAccessConfig.PUBLIC;

  const showIntutoLearning =
    !!canViewEvents &&
    intutoLearningNav.isVisible &&
    !intutoLearningNav.isLoading;

  const showLearningSidebarNav =
    showIntutoLearning && (isOnLearningRoute || learningContextOnEventRoute);
  const showEventsSidebarNav = !!canViewEvents && !showLearningSidebarNav;

  return (
    <>
      <Box
        alignSelf="flex-start"
        as="nav"
        display={{ base: 'none', lg: 'block' }}
        h={`${windowHeight - headerHeight}px`}
        mx="-1"
        overflowY="auto"
        position="sticky"
        px="1"
        py="4"
        top={`${headerHeight}px`}
        w={{ base: 60, xl: 72 }}
        sx={scrollbar}
      >
        {!(showEventsSidebarNav || showLearningSidebarNav) ? null : (
          <Stack spacing="10">
            {showEventsSidebarNav ? (
              <>
                {/* All Events Section */}
                <Stack>
                  <NavigationButton
                    href={buildPath({ filter: 'upcoming' })}
                    leftIcon={<CalendarIcon />}
                    isActive={isAllEvents}
                    onClick={() =>
                      router.push(buildPath({ filter: 'upcoming' }))
                    }
                  >
                    All Events
                  </NavigationButton>

                  <Stack pl="8" spacing="2">
                    <NavigationButton
                      href={buildPath({ filter: 'upcoming' })}
                      isActive={isAllEvents && isUpcoming}
                    >
                      Upcoming Events
                    </NavigationButton>
                    <NavigationButton
                      href={buildPath({ filter: 'past' })}
                      isActive={isAllEvents && isPast}
                    >
                      Past Events
                    </NavigationButton>
                  </Stack>
                </Stack>

                {/* My Events Section */}
                <Stack>
                  <NavigationButton
                    href={buildPath({ filter: 'upcoming', includeMine: true })}
                    leftIcon={<CornerUpLeftIcon />}
                    isActive={isMyEvents}
                    onClick={() =>
                      router.push(
                        buildPath({ filter: 'upcoming', includeMine: true })
                      )
                    }
                  >
                    My Events
                  </NavigationButton>

                  <Stack pl="8" spacing="2">
                    <NavigationButton
                      href={buildPath({
                        filter: 'upcoming',
                        includeMine: true,
                      })}
                      isActive={isMyEvents && isUpcoming}
                    >
                      Upcoming Events
                    </NavigationButton>
                    <NavigationButton
                      href={buildPath({ filter: 'past', includeMine: true })}
                      isActive={isMyEvents && isPast}
                    >
                      Past Events
                    </NavigationButton>
                  </Stack>
                </Stack>

                <Stack>
                  <NavigationButton
                    href={buildXproPath({ filter: 'upcoming' })}
                    leftIcon={<ZapIcon />}
                    isActive={isXproConferences}
                    onClick={() =>
                      router.push(buildXproPath({ filter: 'upcoming' }))
                    }
                  >
                    XPro Conference Events
                  </NavigationButton>

                  <Stack pl="8" spacing="2">
                    <NavigationButton
                      href={buildXproPath({ filter: 'upcoming' })}
                      isActive={isXproConferences && isUpcoming}
                    >
                      Upcoming Events
                    </NavigationButton>
                    <NavigationButton
                      href={buildXproPath({ filter: 'past' })}
                      isActive={isXproConferences && isPast}
                    >
                      Past Events
                    </NavigationButton>
                  </Stack>
                </Stack>
              </>
            ) : null}

            {showLearningSidebarNav ? (
              <>
                <Stack spacing="1">
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="wider"
                    color="gray.500"
                    pl="2"
                  >
                    Learning
                  </Text>
                  <NavigationButton
                    href="/learning"
                    leftIcon={<BookIcon />}
                    isActive={isLearningCourses}
                  >
                    Courses
                  </NavigationButton>
                  <NavigationButton
                    href="/learning/collections"
                    leftIcon={<FolderIcon />}
                    isActive={isLearningCollections}
                  >
                    Collections
                  </NavigationButton>
                </Stack>

                <Stack spacing="1">
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="wider"
                    color="gray.500"
                    pl="2"
                  >
                    My learning
                  </Text>
                  <NavigationButton
                    href="/learning/my-courses"
                    isActive={learningPath === '/learning/my-courses'}
                  >
                    My courses
                  </NavigationButton>
                  <NavigationButton
                    href="/learning/my-collections"
                    isActive={learningPath === '/learning/my-collections'}
                  >
                    My collections
                  </NavigationButton>
                </Stack>
              </>
            ) : null}
          </Stack>
        )}
      </Box>
      <MobileNav
        canViewEvents={!!canViewEvents}
        showEventsSidebarNav={showEventsSidebarNav}
        showLearningSidebarNav={showLearningSidebarNav}
        isAllEvents={isAllEvents}
        isMyEvents={isMyEvents}
        isXproConferences={isXproConferences}
        isUpcoming={isUpcoming}
        isPast={isPast}
        isLearning={
          isLearningCourses ||
          isLearningCollections ||
          learningPath === '/learning/my-courses' ||
          learningPath === '/learning/my-collections'
        }
        isLearningCoursesActive={isLearningCourses}
        learningPath={learningPath}
        onNavigate={(path) => router.push(path)}
        headerHeight={headerHeight}
      />
    </>
  );
};

export const CoffeeMain: React.FC<{ isFullWidth?: boolean }> = (props) => {
  return (
    <Box
      as="main"
      py="4"
      // Explicit pl/pr — avoid px+pl conflict so mobile content clears the 60px rail.
      pl={{ base: 16, lg: 1 }}
      pr="1"
      mx="-1"
      flex="1"
      minW={0}
      w="100%"
      overflowY="auto"
    >
      {props.children}
    </Box>
  );
};

export const CoffeeAside: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();

  const height = useBreakpointValue({
    lg: windowHeight - headerHeight,
  });

  return (
    <Box
      alignSelf="flex-start"
      as="aside"
      h={`${height}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      pr="1"
      pl={{ base: 16, lg: 1 }}
      py="4"
      top={`${headerHeight}px`}
      w={{ lg: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const CoffeePortal = BlueprintPortal;

type CoffeeProps = React.PropsWithChildren<{
  /**
   * Caps width for the Coffee grid (nav + main + aside). Default matches
   * events and other member pages.
   */
  maxContentWidth?: string;
  /**
   * Two-column layout: nav + main only (no right aside). Main column absorbs
   * the width that would have been used by `CoffeeAside` inside the same max width.
   */
  omitThirdColumn?: boolean;
}>;

export const Coffee: React.FC<CoffeeProps> = (props) => {
  const footer = useElementHeight();
  const {
    children,
    maxContentWidth = 'container.xl',
    omitThirdColumn = false,
  } = props;

  return (
    <CoffeeContext.Provider value={{ footerHeight: footer.height }}>
      <Blueprint>
        <Box
          display="grid"
          gridTemplateColumns={{
            base: '1fr',
            lg: omitThirdColumn ? 'auto 1fr' : 'auto 1fr auto',
          }}
          gap={{ base: 2, md: 4 }}
          maxW={maxContentWidth}
          w="100%"
          mx="auto"
          px="4"
          mb={`${footer.height}px`}
        >
          {children}
        </Box>
      </Blueprint>
    </CoffeeContext.Provider>
  );
};
